import React, { useState, useEffect, useCallback } from 'react';
import './AIAgentConfigCard.css';

// ============================================================================
// Types — match the backend API flat column structure
// ============================================================================

type PlacementMode = 'floating_button' | 'welcome_prompt' | 'banner_cta' | 'side_panel' | 'ai_first' | 'disabled';
type PersonalityTone = 'friendly' | 'professional' | 'casual';
type ShowOnPage = 'landing' | 'checkout' | 'reservations';

interface FAQItem {
  question: string;
  answer: string;
}

/** Matches the flat column layout of the aiagent_config table */
interface AIAgentConfig {
  id?: number;
  store_id: number;
  corporate_id?: number;
  agent_type: string;

  // Placement
  placement_mode: PlacementMode;
  auto_open_delay: number;
  show_on_pages: ShowOnPage[];

  // Personality
  welcome_message: string;
  personality_tone: PersonalityTone;
  custom_instructions: string;

  // FAQs (JSON column)
  custom_faqs: FAQItem[];
}

interface AIAgentConfigCardProps {
  store_id: number;
}

// ============================================================================
// Constants
// ============================================================================

const AIAGENT_API_URL = process.env.REACT_APP_AIAGENT_API_URL || 'http://localhost:8005';

const PLACEMENT_OPTIONS: { value: PlacementMode; label: string }[] = [
  { value: 'floating_button', label: 'Floating Button' },
  { value: 'welcome_prompt', label: 'Welcome Prompt' },
  { value: 'banner_cta', label: 'Banner CTA' },
  { value: 'side_panel', label: 'Side Panel' },
  { value: 'ai_first', label: 'AI-First' },
  { value: 'disabled', label: 'Disabled' },
];

const TONE_OPTIONS: { value: PersonalityTone; label: string }[] = [
  { value: 'friendly', label: 'Friendly' },
  { value: 'professional', label: 'Professional' },
  { value: 'casual', label: 'Casual' },
];

const PAGE_OPTIONS: { value: ShowOnPage; label: string }[] = [
  { value: 'landing', label: 'Landing Page' },
  { value: 'checkout', label: 'Checkout' },
  { value: 'reservations', label: 'Reservations' },
];

// Agent type display names and built-in features
const AGENT_TYPE_INFO: Record<string, { label: string; features: string[] }> = {
  booking: {
    label: 'AI Booking Agent',
    features: ['Site search & display', 'Fee calculation & quotes', 'Booking creation', 'Group event booking', 'Membership registration'],
  },
  modification: {
    label: 'AI Reservation Agent',
    features: ['View reservations', 'Modify dates & occupancy', 'Cancel reservations', 'Refund preview'],
  },
  support: {
    label: 'AI Support Agent',
    features: ['FAQ answering', 'Policy information', 'Escalate to human'],
  },
};

const DEFAULT_CONFIG: Omit<AIAgentConfig, 'store_id'> = {
  agent_type: 'booking',
  placement_mode: 'floating_button',
  auto_open_delay: 0,
  show_on_pages: ['landing'],
  welcome_message: '',
  personality_tone: 'friendly',
  custom_instructions: '',
  custom_faqs: [],
};

// ============================================================================
// Component
// ============================================================================

const AIAgentConfigCard: React.FC<AIAgentConfigCardProps> = ({ store_id }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [config, setConfig] = useState<AIAgentConfig>({ store_id, ...DEFAULT_CONFIG });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Subscription state
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [subscriptionTier, setSubscriptionTier] = useState<string | null>(null);
  const [conversationsRemaining, setConversationsRemaining] = useState<number | null>(null);
  const [lockedFields, setLockedFields] = useState<string[]>([]);

  // FAQ editing state
  const [editingFaqIndex, setEditingFaqIndex] = useState<number | null>(null);
  const [faqFormQuestion, setFaqFormQuestion] = useState('');
  const [faqFormAnswer, setFaqFormAnswer] = useState('');
  const [showFaqForm, setShowFaqForm] = useState(false);

  // --------------------------------------------------------------------------
  // API helpers
  // --------------------------------------------------------------------------

  const fetchAiagentConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${AIAGENT_API_URL}/api/aiagent-config/${store_id}`);
      if (!response.ok) {
        if (response.status === 404) {
          setConfig({ store_id, ...DEFAULT_CONFIG });
          return;
        }
        throw new Error(`Failed to fetch AI agent config (${response.status})`);
      }
      const data = await response.json();
      // Capture subscription info
      setSubscriptionStatus(data.subscription_status ?? null);
      setSubscriptionTier(data.subscription_tier ?? null);
      setConversationsRemaining(data.conversations_remaining ?? null);
      setLockedFields(data.locked_fields ?? []);

      // Map API response to our state shape
      setConfig({
        id: data.id,
        store_id: data.store_id ?? store_id,
        corporate_id: data.corporate_id,
        agent_type: data.agent_type ?? 'booking',
        placement_mode: data.placement_mode ?? 'floating_button',
        auto_open_delay: data.auto_open_delay ?? 0,
        show_on_pages: data.show_on_pages ?? ['landing'],
        welcome_message: data.welcome_message ?? '',
        personality_tone: data.personality_tone ?? 'friendly',
        custom_instructions: data.custom_instructions ?? '',
        custom_faqs: data.custom_faqs ?? [],
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load AI agent configuration');
      console.error('Failed to load AI agent config:', err);
    } finally {
      setLoading(false);
    }
  }, [store_id]);

  const saveAiagentConfig = async () => {
    setSaving(true);
    setSaveStatus('idle');
    try {
      const response = await fetch(`${AIAGENT_API_URL}/api/aiagent-config/${store_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          placement_mode: config.placement_mode,
          auto_open_delay: config.auto_open_delay,
          show_on_pages: config.show_on_pages,
          welcome_message: config.welcome_message,
          personality_tone: config.personality_tone,
          custom_instructions: config.custom_instructions,
          custom_faqs: config.custom_faqs,
        }),
      });
      if (!response.ok) {
        throw new Error(`Failed to save AI agent config (${response.status})`);
      }
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err) {
      setSaveStatus('error');
      alert(err instanceof Error ? err.message : 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchAiagentConfig();
  }, [fetchAiagentConfig]);

  // --------------------------------------------------------------------------
  // Handlers
  // --------------------------------------------------------------------------

  const handleFieldChange = (field: keyof AIAgentConfig, value: any) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handlePageToggle = (page: ShowOnPage) => {
    setConfig(prev => {
      const current = prev.show_on_pages;
      const next = current.includes(page)
        ? current.filter(p => p !== page)
        : [...current, page];
      return { ...prev, show_on_pages: next };
    });
  };


  // --------------------------------------------------------------------------
  // FAQ handlers
  // --------------------------------------------------------------------------

  const openAddFaq = () => {
    setEditingFaqIndex(null);
    setFaqFormQuestion('');
    setFaqFormAnswer('');
    setShowFaqForm(true);
  };

  const openEditFaq = (index: number) => {
    const faq = config.custom_faqs[index];
    setEditingFaqIndex(index);
    setFaqFormQuestion(faq.question);
    setFaqFormAnswer(faq.answer);
    setShowFaqForm(true);
  };

  const handleSaveFaq = () => {
    if (!faqFormQuestion.trim() || !faqFormAnswer.trim()) {
      alert('Please enter both a question and an answer');
      return;
    }
    const newFaq: FAQItem = { question: faqFormQuestion.trim(), answer: faqFormAnswer.trim() };
    setConfig(prev => {
      const faqs = [...prev.custom_faqs];
      if (editingFaqIndex !== null) {
        faqs[editingFaqIndex] = newFaq;
      } else {
        faqs.push(newFaq);
      }
      return { ...prev, custom_faqs: faqs };
    });
    setShowFaqForm(false);
  };

  const handleDeleteFaq = (index: number) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;
    setConfig(prev => ({
      ...prev,
      custom_faqs: prev.custom_faqs.filter((_, i) => i !== index),
    }));
  };

  const cancelFaqForm = () => {
    setShowFaqForm(false);
  };

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------

  return (
    <div className="config-card">
      <div
        className="config-card-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="config-card-title">
          <span className="config-card-icon">AI</span>
          <h2>{AGENT_TYPE_INFO[config.agent_type]?.label ?? 'AI Booking Agent'}</h2>
        </div>
        <div className="config-card-header-right">
          <span className="config-card-db-label">aiagent-service/config</span>
          <span className={`config-card-toggle ${isExpanded ? 'expanded' : ''}`}>
            {String.fromCharCode(9660)}
          </span>
        </div>
      </div>

      {isExpanded && (
        <div className="config-card-content" style={{ maxHeight: 'none' }}>
          {loading ? (
            <div className="loading-state">Loading AI agent configuration...</div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
              <button className="btn btn-primary btn-sm" onClick={fetchAiagentConfig}>
                Retry
              </button>
            </div>
          ) : subscriptionStatus === null || subscriptionStatus === 'none' ? (
            <div className="aiagent-section" style={{ textAlign: 'center', padding: '40px 20px' }}>
              <h3 style={{ marginBottom: 8 }}>AI Booking Agent — Not Subscribed</h3>
              <p style={{ color: 'var(--text-muted)' }}>
                This store does not have an active AI Booking Agent subscription.
                Contact your HCP representative to get started.
              </p>
            </div>
          ) : subscriptionStatus === 'expired' || subscriptionStatus === 'cancelled' ? (
            <div className="aiagent-section" style={{ textAlign: 'center', padding: '40px 20px' }}>
              <h3 style={{ marginBottom: 8 }}>AI Booking Agent — Subscription {subscriptionStatus === 'expired' ? 'Expired' : 'Cancelled'}</h3>
              <p style={{ color: 'var(--text-muted)' }}>
                Your AI Booking Agent subscription is {subscriptionStatus}. Contact your HCP representative to renew.
              </p>
            </div>
          ) : (
            <>
              {/* Subscription info banner */}
              <div className="aiagent-section" style={{ background: 'var(--bg-secondary, #f8f9fa)', padding: '12px 16px', borderRadius: 8, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong>AI Booking Agent</strong>
                  {subscriptionTier && <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 4, background: '#e0e7ff', color: '#3730a3', fontSize: '0.85rem', textTransform: 'capitalize' }}>{subscriptionTier}</span>}
                  {subscriptionStatus === 'trial' && <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 4, background: '#fef3c7', color: '#92400e', fontSize: '0.85rem' }}>Trial</span>}
                </div>
                {conversationsRemaining !== null && (
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                    {conversationsRemaining.toLocaleString()} conversations remaining this month
                  </span>
                )}
              </div>

              {/* ---- Placement & Display ---- */}
              <div className="aiagent-section">
                <h3 className="aiagent-section-title">Placement &amp; Display</h3>

                <div className="form-row">
                  <div className="form-group">
                    <label>Placement Mode</label>
                    <select
                      value={config.placement_mode}
                      onChange={e => handleFieldChange('placement_mode', e.target.value)}
                    >
                      {PLACEMENT_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Auto-Open Delay (seconds)</label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={config.auto_open_delay}
                      onChange={e => handleFieldChange('auto_open_delay', parseInt(e.target.value, 10) || 0)}
                    />
                    <small className="form-hint">0 = do not auto-open</small>
                  </div>
                </div>

                <div className="form-group">
                  <label>Show on Pages</label>
                  <div className="checkbox-list">
                    {PAGE_OPTIONS.map(opt => (
                      <div className="checkbox-item" key={opt.value}>
                        <input
                          type="checkbox"
                          id={`page-${opt.value}`}
                          checked={config.show_on_pages.includes(opt.value)}
                          onChange={() => handlePageToggle(opt.value)}
                        />
                        <label htmlFor={`page-${opt.value}`}>{opt.label}</label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ---- Personality & Messaging ---- */}
              <div className="aiagent-section">
                <h3 className="aiagent-section-title">Personality &amp; Messaging</h3>

                <div className="form-group">
                  <label>
                    Welcome Message
                    {lockedFields.includes('welcome_message') && <span style={{ marginLeft: 6, fontSize: '0.8rem', color: '#999' }} title="Managed by your corporate administrator">🔒</span>}
                  </label>
                  <textarea
                    value={config.welcome_message}
                    onChange={e => handleFieldChange('welcome_message', e.target.value)}
                    placeholder="e.g., Hi there! How can I help you book your perfect campsite today?"
                    rows={3}
                    disabled={lockedFields.includes('welcome_message')}
                    style={lockedFields.includes('welcome_message') ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                  />
                </div>

                <div className="form-group">
                  <label>
                    Personality Tone
                    {lockedFields.includes('personality_tone') && <span style={{ marginLeft: 6, fontSize: '0.8rem', color: '#999' }} title="Managed by your corporate administrator">🔒</span>}
                  </label>
                  <select
                    value={config.personality_tone}
                    onChange={e => handleFieldChange('personality_tone', e.target.value)}
                    disabled={lockedFields.includes('personality_tone')}
                    style={lockedFields.includes('personality_tone') ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                  >
                    {TONE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>
                    Custom Instructions
                    {lockedFields.includes('custom_instructions') && <span style={{ marginLeft: 6, fontSize: '0.8rem', color: '#999' }} title="Managed by your corporate administrator">🔒</span>}
                  </label>
                  <textarea
                    value={config.custom_instructions}
                    onChange={e => handleFieldChange('custom_instructions', e.target.value)}
                    placeholder="Additional instructions for the AI agent (e.g., always mention pet policy, highlight seasonal specials)"
                    rows={4}
                    disabled={lockedFields.includes('custom_instructions')}
                    style={lockedFields.includes('custom_instructions') ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                  />
                </div>
              </div>

              {/* ---- Built-in Features (read-only) ---- */}
              <div className="aiagent-section">
                <h3 className="aiagent-section-title">Built-in Features</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 12 }}>
                  These features are built into the {AGENT_TYPE_INFO[config.agent_type]?.label ?? config.agent_type} and cannot be changed. Managed by your subscription tier.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {(AGENT_TYPE_INFO[config.agent_type]?.features ?? []).map((feature, i) => (
                    <span key={i} style={{
                      padding: '6px 12px',
                      borderRadius: 6,
                      background: 'var(--bg-secondary, #f0f4f8)',
                      color: 'var(--text-primary, #333)',
                      fontSize: '0.85rem',
                      border: '1px solid var(--border-color, #e2e8f0)',
                    }}>
                      {feature}
                    </span>
                  ))}
                </div>
              </div>

              {/* ---- Custom FAQs ---- */}
              <div className="aiagent-section">
                <h3 className="aiagent-section-title">Custom FAQs</h3>

                {config.custom_faqs.length === 0 && !showFaqForm && (
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                    No custom FAQs configured. Add FAQs to help the AI answer common questions.
                  </p>
                )}

                {config.custom_faqs.length > 0 && (
                  <div className="aiagent-faq-list">
                    {config.custom_faqs.map((faq, index) => (
                      <div className="aiagent-faq-item" key={index}>
                        <div className="aiagent-faq-content">
                          <div className="aiagent-faq-question">Q: {faq.question}</div>
                          <div className="aiagent-faq-answer">A: {faq.answer}</div>
                        </div>
                        <div className="config-actions">
                          <button
                            className="btn btn-text btn-sm"
                            onClick={() => openEditFaq(index)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-text btn-sm btn-danger"
                            onClick={() => handleDeleteFaq(index)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {showFaqForm && (
                  <div className="aiagent-faq-form">
                    <div className="form-group">
                      <label>Question *</label>
                      <input
                        type="text"
                        value={faqFormQuestion}
                        onChange={e => setFaqFormQuestion(e.target.value)}
                        placeholder="e.g., What is your cancellation policy?"
                      />
                    </div>
                    <div className="form-group">
                      <label>Answer *</label>
                      <textarea
                        value={faqFormAnswer}
                        onChange={e => setFaqFormAnswer(e.target.value)}
                        placeholder="e.g., Cancellations made 48 hours before check-in receive a full refund."
                        rows={3}
                      />
                    </div>
                    <div className="aiagent-faq-form-actions">
                      <button className="btn btn-secondary btn-sm" onClick={cancelFaqForm}>
                        Cancel
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={handleSaveFaq}>
                        {editingFaqIndex !== null ? 'Update FAQ' : 'Add FAQ'}
                      </button>
                    </div>
                  </div>
                )}

                {!showFaqForm && (
                  <div style={{ marginTop: 'var(--spacing-md)' }}>
                    <button className="btn btn-primary btn-sm" onClick={openAddFaq}>
                      + Add FAQ
                    </button>
                  </div>
                )}
              </div>

              {/* ---- Save ---- */}
              <div className="config-card-footer">
                {saveStatus === 'success' && (
                  <span style={{ color: 'var(--status-confirmed)', marginRight: 'var(--spacing-md)', fontSize: '0.9rem' }}>
                    Configuration saved successfully
                  </span>
                )}
                {saveStatus === 'error' && (
                  <span style={{ color: 'var(--status-cancelled)', marginRight: 'var(--spacing-md)', fontSize: '0.9rem' }}>
                    Failed to save configuration
                  </span>
                )}
                <button
                  className="btn btn-primary"
                  onClick={saveAiagentConfig}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Configuration'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AIAgentConfigCard;
